/* ========== BXGY GIFT START ========== */
/**
 * Buy X Get Y (custom.gift_product) sepet senkronizasyonu
 * - X eklenince Y'yi aynı adet kadar ekler / arttırır
 * - X adedi değişince Y'nin senkron kısmını orantılı günceller (extra Y korunur)
 * - Y adedi X altına inemez / Y silinemez → uyarı modalı
 *
 * Bu dosya framework-agnostic bir singleton'dur (window.KaracaBxgy).
 * Alpine.js state'ine bağımlı değildir; cartPage (Alpine) ve component-cart.js
 * (vanilla custom element) bu API'yi çağırarak entegre olur.
 */
(function () {
  'use strict';

  const PROP_IS_GIFT = '_bxgy_is_gift';
  const PROP_PARENT_PRODUCT_ID = '_bxgy_parent_product_id';
  const PROP_PARENT_VARIANT_ID = '_bxgy_parent_variant_id';
  const PROP_GIFT_VARIANT_ID = '_bxgy_gift_variant_id';

  const BxgyGift = {
    _busy: false,
    _syncQueue: Promise.resolve(),
    _pendingGiftAdd: null,

    async getCart() {
      const res = await fetch(`${KROWN.settings.routes.cart_url}.js`);
      return res.json();
    },

    /**
     * DOM'daki data-bxgy-gift-variant-id attribute'lerinden
     * ve line item property'lerinden parent → gift eşlemesini çıkarır
     */
    buildGiftMapFromDom() {
      const map = {}; // giftVariantId -> [{ key, qty, productId }]
      document.querySelectorAll('[data-js-cart-item][data-bxgy-gift-variant-id]').forEach((el) => {
        const giftVariantId = String(el.dataset.bxgyGiftVariantId);
        if (!giftVariantId) return;
        if (!map[giftVariantId]) map[giftVariantId] = [];
        map[giftVariantId].push({
          key: el.dataset.id,
          qty: parseInt(el.dataset.qty, 10) || 0,
          productId: el.dataset.productId
        });
      });
      return map;
    },

    getParentTotalForGift(cart, giftVariantId, overrideKey, overrideQty) {
      const giftVid = String(giftVariantId);
      let total = 0;
      let foundOverride = false;

      // 1) DOM attribute'leri (metafield tabanlı)
      document.querySelectorAll('[data-js-cart-item][data-bxgy-gift-variant-id]').forEach((el) => {
        if (String(el.dataset.bxgyGiftVariantId) !== giftVid) return;
        const lineKey = el.dataset.id;
        if (overrideKey && lineKey === overrideKey) {
          total += Math.max(0, overrideQty);
          foundOverride = true;
          return;
        }
        // DOM reRender öncesi stale olabilir — cart.js varsa onu kullan
        if (cart && cart.items) {
          const cartLine = cart.items.find((i) => i.key === lineKey);
          if (cartLine) total += cartLine.quantity;
          return;
        }
        total += parseInt(el.dataset.qty, 10) || 0;
      });

      // Parent DOM'dan silinmişse (full page remove) override'ı elle ekle
      if (overrideKey && !foundOverride && typeof overrideQty === 'number') {
        total += Math.max(0, overrideQty);
      }

      // 2) Property tabanlı (DOM'da attribute yoksa yedek)
      if (total === 0 && !overrideKey && cart && cart.items) {
        cart.items.forEach((item) => {
          const props = item.properties || {};
          if (String(props[PROP_GIFT_VARIANT_ID] || '') === giftVid) {
            total += item.quantity;
          }
        });
      }

      return total;
    },

    /** cart/change veya cart.js cevabından satır adetlerini anında güncelle (reRender öncesi) */
    patchCartLinesFromResponse(cart) {
      if (!cart || !cart.items) return;

      cart.items.forEach((line) => {
        const el = document.querySelector(`[data-js-cart-item][data-id="${line.key}"]`);
        if (!el) return;
        el.dataset.qty = String(line.quantity);
        el.querySelectorAll('.qty').forEach((input) => {
          input.value = line.quantity;
          input.dataset.oldvalue = line.quantity;
        });
      });

      if (typeof cart.item_count === 'number') {
        document.querySelectorAll('[data-header-cart-count]').forEach((elm) => {
          elm.textContent = cart.item_count;
        });
        const hiddenCount = document.querySelector('#AjaxCartForm [data-cart-count]');
        if (hiddenCount) hiddenCount.textContent = cart.item_count;
      }
    },

    findGiftItem(cart, giftVariantId) {
      const giftVid = parseInt(giftVariantId, 10);
      return (cart.items || []).find((item) => item.variant_id === giftVid) || null;
    },

    _variantCache: {},

    /** Variant stok bilgisi (/variants/{id}.js) — cache'li */
    async getVariantInfo(variantId) {
      const vid = String(variantId);
      if (this._variantCache[vid]) return this._variantCache[vid];
      try {
        const res = await fetch(`/variants/${vid}.js`);
        if (!res.ok) return { tracked: false, quantity: Infinity, policy: 'continue' };
        const v = await res.json();
        const info = {
          tracked: !!v.inventory_management,
          quantity: typeof v.inventory_quantity === 'number' ? v.inventory_quantity : Infinity,
          policy: v.inventory_policy || 'deny'
        };
        this._variantCache[vid] = info;
        return info;
      } catch (e) {
        return { tracked: false, quantity: Infinity, policy: 'continue' };
      }
    },

    /** DOM'daki inventory attribute'lerinden stok limiti (cart line) */
    getStockLimitFromDom(itemEl) {
      if (!itemEl || itemEl.dataset.inventoryTracked !== 'true') return Infinity;
      if (itemEl.dataset.inventoryPolicy === 'continue') return Infinity;
      const qty = parseInt(itemEl.dataset.inventoryQty, 10);
      return isNaN(qty) ? Infinity : Math.max(0, qty);
    },

    /** Gift variant stok limiti — parent satırındaki data-bxgy-gift-inventory-* */
    getGiftStockLimitFromParentDom(parentEl) {
      if (!parentEl || parentEl.dataset.bxgyGiftInventoryTracked !== 'true') return Infinity;
      if (parentEl.dataset.bxgyGiftInventoryPolicy === 'continue') return Infinity;
      const qty = parseInt(parentEl.dataset.bxgyGiftInventoryQty, 10);
      return isNaN(qty) ? Infinity : Math.max(0, qty);
    },

    /** Sepetteki toplam adet (aynı variant) */
    getVariantQtyInCart(cart, variantId) {
      const vid = parseInt(variantId, 10);
      return (cart.items || []).reduce((sum, item) => {
        return item.variant_id === vid ? sum + item.quantity : sum;
      }, 0);
    },

    /** Variant için sepette izin verilen maksimum toplam adet */
    async getMaxTotalQtyForVariant(variantId, cart) {
      const info = await this.getVariantInfo(variantId);
      if (!info.tracked || info.policy === 'continue') return Infinity;
      return Math.max(0, info.quantity);
    },

    /** Hedef adedi variant stok limitine göre kırp */
    async capQtyByVariantStock(variantId, desiredQty, cart) {
      const maxTotal = await this.getMaxTotalQtyForVariant(variantId, cart);
      if (maxTotal === Infinity) return desiredQty;
      return Math.min(desiredQty, maxTotal);
    },

    /** Gift sync için stok limiti — önce DOM, gerekirse API */
    async capGiftQtyForSync(giftVariantId, desiredQty, cart, parentKey) {
      let capped = desiredQty;
      let hasDomLimit = false;

      const parentEl = parentKey
        ? document.querySelector(`[data-js-cart-item][data-id="${parentKey}"]`)
        : null;
      if (parentEl) {
        const parentDomLimit = this.getGiftStockLimitFromParentDom(parentEl);
        if (parentDomLimit !== Infinity) {
          capped = Math.min(capped, parentDomLimit);
          hasDomLimit = true;
        }
      }

      const giftEl = document.querySelector(
        `[data-js-cart-item][data-bxgy-is-gift-target][data-variant-id="${giftVariantId}"]`
      );
      if (giftEl) {
        const giftDomLimit = this.getStockLimitFromDom(giftEl);
        if (giftDomLimit !== Infinity) {
          capped = Math.min(capped, giftDomLimit);
          hasDomLimit = true;
        }
      }

      if (!hasDomLimit) {
        capped = await this.capQtyByVariantStock(giftVariantId, capped, cart);
      }

      return capped;
    },

    _pendingWarning: null,

    queueWarning(type) {
      this._pendingWarning = type;
    },

    flushPendingWarning() {
      if (!this._pendingWarning) return;
      const type = this._pendingWarning;
      this._pendingWarning = null;
      this.showWarning(type);
    },

    /** Hediye uyarısı varsa önce onu göster, kapandıktan sonra callback çalıştır */
    flushPendingWarningBefore(callback) {
      if (!this._pendingWarning) {
        if (typeof callback === 'function') callback();
        return;
      }

      const bxgyModal = document.getElementById('bxgyGiftWarningModal');
      const onDone = () => {
        if (typeof callback === 'function') callback();
      };

      if (bxgyModal) {
        bxgyModal.addEventListener('close', onDone, { once: true });
      }

      this.flushPendingWarning();

      if (!bxgyModal || !bxgyModal.open) {
        bxgyModal?.removeEventListener('close', onDone);
        onDone();
      }
    },

    parseGiftStockMeta(el) {
      if (!el || el.dataset.bxgyGiftInventoryTracked !== 'true') {
        return { tracked: false };
      }
      const policy = el.dataset.bxgyGiftInventoryPolicy || 'deny';
      const qty = parseInt(el.dataset.bxgyGiftInventoryQty, 10);
      return {
        tracked: true,
        policy,
        qty: isNaN(qty) ? Infinity : Math.max(0, qty)
      };
    },

    async capGiftQtyForAdd(giftVariantId, desiredQty, cart, stockMeta) {
      if (stockMeta?.tracked && stockMeta.policy !== 'continue') {
        return Math.min(desiredQty, stockMeta.qty);
      }
      return this.capQtyByVariantStock(giftVariantId, desiredQty, cart);
    },

    queueGiftStockWarning(desiredQty, cappedQty, parentAddQty) {
      if (cappedQty < desiredQty || cappedQty < parentAddQty) {
        this.queueWarning('gift_stock_partial_sync');
      }
    },

    verifyGiftStockWarning(giftVariantId, desiredQty, cartResponse) {
      if (!cartResponse || !cartResponse.items) return;
      const giftLine = this.findGiftItem(cartResponse, giftVariantId);
      const actualQty = giftLine ? giftLine.quantity : 0;
      if (actualQty < desiredQty) {
        this.queueWarning('gift_stock_partial_sync');
      }
    },

    shouldShowPartialSyncWarning(oldQty, newQty, newParentTotal, targetGiftQty, giftQty) {
      if (newQty <= oldQty) return false;
      if (newParentTotal > targetGiftQty) return true;
      if (typeof giftQty === 'number' && newParentTotal > giftQty) return true;
      return false;
    },

    /** cart/change cevabından gerçek line adedini al — satır sepetten silindiyse fallback kullan */
    getActualLineQty(cartResponse, lineKey, fallbackQty) {
      if (!cartResponse || cartResponse.status === 422) return fallbackQty;
      const line = (cartResponse.items || []).find((i) => i.key === lineKey);
      if (!line) return typeof fallbackQty === 'number' ? fallbackQty : 0;
      return line.quantity;
    },

    /** Gift satırındaki senkron (parent'a bağlı) minimum adet */
    getParentTotalForGiftItem(cart, giftVariantId, giftItemKey) {
      const giftVid = String(giftVariantId);
      let parentTotal = 0;

      document.querySelectorAll('[data-js-cart-item][data-bxgy-gift-variant-id]').forEach((el) => {
        if (String(el.dataset.bxgyGiftVariantId) === giftVid) {
          parentTotal += parseInt(el.dataset.qty, 10) || 0;
        }
      });

      if (parentTotal === 0 && cart && cart.items) {
        cart.items.forEach((p) => {
          const props = p.properties || {};
          if (String(props[PROP_GIFT_VARIANT_ID] || '') === giftVid) {
            parentTotal += p.quantity;
          }
        });
      }

      return parentTotal;
    },

    isGiftLineItem(itemEl, cartItem) {
      if (itemEl && itemEl.hasAttribute('data-bxgy-is-gift-target')) return true;
      if (cartItem && cartItem.properties && cartItem.properties[PROP_IS_GIFT] === 'true') return true;
      return false;
    },

    getGiftVariantIdForItem(itemEl) {
      return itemEl ? itemEl.dataset.bxgyGiftVariantId : null;
    },

    _locale: null,

    /** Locale metinleri — holly-bxgy-gift-modal.liquid içindeki JSON'dan */
    getLocale() {
      if (this._locale) return this._locale;

      const localeEl = document.getElementById('bxgy-gift-locale');
      if (localeEl) {
        try {
          this._locale = JSON.parse(localeEl.textContent);
          return this._locale;
        } catch (e) {
          console.warn('[BxgyGift] locale parse error', e);
        }
      }

      const modal = document.getElementById('bxgyGiftWarningModal');
      if (modal) {
        this._locale = {
          remove: { title: modal.dataset.title || '', message: modal.dataset.removeMessage || '' },
          qty: { title: modal.dataset.title || '', message: modal.dataset.qtyMessage || '' },
          gift_stock_partial_sync: {
            title: modal.dataset.giftStockPartialSyncTitle || '',
            message: modal.dataset.giftStockPartialSyncMessage || ''
          }
        };
        return this._locale;
      }

      this._locale = {};
      return this._locale;
    },

    getWarningCopy(type) {
      const locale = this.getLocale();
      return locale[type] || locale.qty || { title: '', message: '' };
    },

    showWarning(type) {
      const modal = document.getElementById('bxgyGiftWarningModal');
      const { title, message } = this.getWarningCopy(type);
      if (!modal || !message) return;

      const titleEl = modal.querySelector('[data-bxgy-title]');
      const msgEl = modal.querySelector('[data-bxgy-message]');

      if (titleEl) titleEl.textContent = title;
      if (msgEl) msgEl.textContent = message;

      if (typeof modal.showModal === 'function') {
        modal.showModal();
      } else {
        modal.setAttribute('open', '');
        modal.classList.remove('hidden');
      }
    },

    closeWarning() {
      const modal = document.getElementById('bxgyGiftWarningModal');
      if (!modal) return;
      if (typeof modal.close === 'function') {
        modal.close();
      } else {
        modal.removeAttribute('open');
        modal.classList.add('hidden');
      }
    },

    /**
     * X ürünü sepete eklendikten sonra Y'yi ekler / adedini arttırır
     */
    async addGiftForParent({ giftVariantId, quantity, parentProductId, parentVariantId, giftStockMeta }) {
      const task = this._addGiftForParentNow({ giftVariantId, quantity, parentProductId, parentVariantId, giftStockMeta });
      this._pendingGiftAdd = task.finally(() => {
        if (this._pendingGiftAdd === task) this._pendingGiftAdd = null;
      });
      return this._pendingGiftAdd;
    },

    waitForPendingGiftAdd() {
      return this._pendingGiftAdd || Promise.resolve();
    },

    async _addGiftForParentNow({ giftVariantId, quantity, parentProductId, parentVariantId, giftStockMeta }) {
      if (!giftVariantId || !quantity || quantity < 1) return null;

      const cart = await this.getCart();
      const existing = this.findGiftItem(cart, giftVariantId);
      const desiredQty = existing ? existing.quantity + quantity : quantity;
      const cappedQty = await this.capGiftQtyForAdd(giftVariantId, desiredQty, cart, giftStockMeta);

      this.queueGiftStockWarning(desiredQty, cappedQty, quantity);

      if (cappedQty < 1) return null;

      if (existing) {
        if (existing.quantity === cappedQty) return null;
        const res = await fetch(KROWN.settings.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({
            id: existing.key,
            quantity: cappedQty
          })
        });
        const result = await res.json();
        this.verifyGiftStockWarning(giftVariantId, desiredQty, result);
        return result;
      }

      const res = await fetch(`${KROWN.settings.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          items: [
            {
              id: Number(giftVariantId),
              quantity: cappedQty,
              properties: {
                [PROP_IS_GIFT]: 'true',
                [PROP_PARENT_PRODUCT_ID]: String(parentProductId || ''),
                [PROP_PARENT_VARIANT_ID]: String(parentVariantId || '')
              }
            }
          ]
        })
      });
      const result = await res.json();
      this.verifyGiftStockWarning(giftVariantId, desiredQty, result);
      return result;
    },

    /**
     * Parent qty change sonrası gift sync — cart/change cevabı ile birlikte çağır.
     * 422 ise sync yapma; başarılıysa gerçek parent adedini kullan.
     */
    syncGiftAfterParentChange(parentKey, oldQty, requestedNewQty, giftVariantId, cartResponse) {
      if (!giftVariantId) return Promise.resolve(null);

      if (cartResponse && cartResponse.status === 422) {
        return Promise.resolve(null);
      }

      const actualNewQty = cartResponse
        ? this.getActualLineQty(cartResponse, parentKey, requestedNewQty)
        : requestedNewQty;

      this._syncQueue = this._syncQueue
        .then(() => this._syncGiftAfterParentChangeNow(parentKey, oldQty, actualNewQty, giftVariantId, cartResponse))
        .catch((e) => {
          console.warn('[BxgyGift] sync queue error', e);
        });

      return this._syncQueue;
    },

    async _syncGiftAfterParentChangeNow(parentKey, oldQty, newQty, giftVariantId, cartResponse) {
      this._busy = true;

      try {
        const cart = cartResponse && cartResponse.items
          ? cartResponse
          : await this.getCart();
        const giftItem = this.findGiftItem(cart, giftVariantId);
        const oldParentTotal = this.getParentTotalForGift(cart, giftVariantId, parentKey, oldQty);
        const newParentTotal = this.getParentTotalForGift(cart, giftVariantId, parentKey, newQty);
        const giftQtyBefore = giftItem ? giftItem.quantity : 0;

        // Extra = gift toplamı - eski senkron (parent) toplamı
        const extra = giftItem ? Math.max(0, giftItem.quantity - oldParentTotal) : 0;
        // Y asla X (parent toplamı) altına inemez
        const idealTargetGiftQty = Math.max(extra + newParentTotal, newParentTotal);
        let targetGiftQty = idealTargetGiftQty;
        // Hediye ürün stok limitine göre kırp (X artabilir, Y limitte kalabilir)
        targetGiftQty = await this.capGiftQtyForSync(giftVariantId, targetGiftQty, cart, parentKey);

        const warnPartialSync = () => {
          if (this.shouldShowPartialSyncWarning(oldQty, newQty, newParentTotal, targetGiftQty, giftQtyBefore)) {
            this.queueWarning('gift_stock_partial_sync');
          }
        };

        if (!giftItem) {
          if (targetGiftQty > 0) {
            const result = await this.addGiftForParent({
              giftVariantId,
              quantity: targetGiftQty,
              parentProductId: '',
              parentVariantId: ''
            });
            warnPartialSync();
            return result;
          }
          warnPartialSync();
          return null;
        }

        if (giftItem.quantity === targetGiftQty) {
          warnPartialSync();
          return null;
        }

        const res = await fetch(KROWN.settings.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({
            id: giftItem.key,
            quantity: targetGiftQty
          })
        });
        const result = await res.json();
        if (result.status === 422) {
          console.warn('[BxgyGift] gift stock limit reached');
          warnPartialSync();
        } else {
          const updatedGiftQty = this.getActualLineQty(result, giftItem.key, targetGiftQty);
          if (this.shouldShowPartialSyncWarning(oldQty, newQty, newParentTotal, targetGiftQty, updatedGiftQty)) {
            this.queueWarning('gift_stock_partial_sync');
          }
        }
        return result;
      } finally {
        this._busy = false;
      }
    },

    /**
     * Gift (Y) adet değişimini doğrular.
     * Yalnızca senkron kısmı (parent altına inme / silme) engellenir.
     * Parent adedinin üstündeki bağımsız artışa müdahale edilmez.
     */
    async validateGiftQtyChange(itemKey, newQty, giftVariantIdHint, oldQtyHint) {
      const cart = await this.getCart();
      const item = (cart.items || []).find((i) => i.key === itemKey);
      if (!item) return { allowed: true };

      const itemEl = document.querySelector(`[data-js-cart-item][data-id="${itemKey}"]`);
      const oldQty = typeof oldQtyHint === 'number'
        ? oldQtyHint
        : (itemEl ? parseInt(itemEl.dataset.qty, 10) : item.quantity) || 0;

      const giftVid = String(item.variant_id);
      const parentTotal = this.getParentTotalForGiftItem(cart, giftVid, itemKey);

      // Sepette parent yok — tamamen bağımsız ürün, BXGY müdahalesi yok
      if (parentTotal <= 0) {
        return { allowed: true };
      }

      /* ========== BXGY GIFT START: stok kaynaklı kilit ayrımı ========== */
      // İstenen adet hediyenin kendi gerçek stoğunu aşıyorsa (parentTotal'a göre
      // değil, doğrudan Shopify stoğuna göre), isteği Shopify'a göndermeden
      // BXGY'nin "hediye stokta yok" modalıyla engelle. Aksi halde istek Shopify'a
      // gidip 422 dönüyor ve temanın varsayılan/ham stok hatası gösteriliyor,
      // ya da (parentTotal'a tam eşit istenirken) hiç engellenmeden Shopify'a
      // gidip aynı ham hatayı tetikliyor.
      const stockLimit = this.getStockLimitFromDom(itemEl);
      if (stockLimit !== Infinity && newQty > stockLimit) {
        return {
          allowed: false,
          type: 'gift_stock_partial_sync',
          parentTotal
        };
      }
      /* ========== BXGY GIFT END ========== */

      // Parent varken Y tamamen silinemez / parent altına inemez (senkron kısım)
      if (newQty < parentTotal) {
        return {
          allowed: false,
          type: newQty === 0 ? 'remove' : 'qty',
          parentTotal
        };
      }

      // newQty >= parentTotal: artış/azalış serbest (bağımsız/extra kısım — stok Shopify'a bırakılır)
      return { allowed: true, parentTotal };
    },

    /**
     * Sepetteki tüm parent/gift ilişkilerini kontrol eder ve düzeltir.
     * Parent silinince (full page nav) gift'in senkron kısmını temizler.
     */
    async enforceCartGiftSync() {
      if (this._busy) return;
      const parents = document.querySelectorAll('[data-js-cart-item][data-bxgy-gift-variant-id]');
      if (!parents.length && !document.querySelector('[data-bxgy-is-gift-target]')) return;

      this._busy = true;
      try {
        const cart = await this.getCart();
        const giftTotals = {}; // giftVariantId -> required synced qty (sum of parents)

        parents.forEach((el) => {
          const giftVid = String(el.dataset.bxgyGiftVariantId);
          const qty = parseInt(el.dataset.qty, 10) || 0;
          giftTotals[giftVid] = (giftTotals[giftVid] || 0) + qty;
        });

        // Property tabanlı parent'lar (DOM attribute yoksa)
        (cart.items || []).forEach((item) => {
          const props = item.properties || {};
          const giftVid = props[PROP_GIFT_VARIANT_ID];
          if (!giftVid) return;
          const key = String(giftVid);
          if (giftTotals[key] === undefined) {
            giftTotals[key] = 0;
          }
          // DOM'da zaten sayıldıysa çift sayma
          const inDom = document.querySelector(`[data-js-cart-item][data-id="${item.key}"][data-bxgy-gift-variant-id]`);
          if (!inDom) {
            giftTotals[key] += item.quantity;
          }
        });

        let changed = false;
        const processedGifts = new Set();

        for (const giftVid of Object.keys(giftTotals)) {
          processedGifts.add(giftVid);
          const required = giftTotals[giftVid];
          const giftItem = this.findGiftItem(cart, giftVid);
          if (!giftItem) {
            if (required > 0) {
              await this.addGiftForParent({ giftVariantId: giftVid, quantity: required });
              changed = true;
            }
            continue;
          }
          // Extra korunur: gift asla required altına inmemeli; üstündeyse dokunma
          // Ancak parent azaldıysa/silindiyse: extra = current - previousRequired bilinmiyor.
          // Sayfa yükünde yalnızca gift < required ise tamamla.
          // Parent silinmişse giftTotals'da giftVid olmayabilir — aşağıda orphan kontrolü.
          if (giftItem.quantity < required) {
            await fetch(KROWN.settings.routes.cart_change_url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
              body: JSON.stringify({ id: giftItem.key, quantity: required })
            });
            changed = true;
          }
        }

        // Orphan gift property'li satırlar: parent yoksa synced kısmı kaldırılamaz
        // (extra korunmalı ama synced miktar bilinmiyor → property'li gift'lerde
        //  parent yoksa ve başka parent da yoksa quantity'yi bırak — kullanıcı kampanya dışı almış olabilir)
        // Parent silindikten sonra DOM'da gift target var ama giftTotals'da parent qty=0.
        // Bu durumda synced kısmı düşürmek için sessionStorage ile son parent qty tutabiliriz.
        // Pratik çözüm: parent DOM'da yoksa ve giftTotals[vid] undefined ise,
        // property _bxgy_is_gift olan satırın qty'sini 0 yapmayız (extra olabilir).
        // Parent AJAX ile silindiğinde syncGiftAfterParentChange zaten halleder.
        // Full page parent remove için: cart attributes / sessionStorage

        if (changed) {
          const cartForm = document.getElementById('AjaxCartForm');
          if (cartForm && typeof cartForm.reRenderCart === 'function') {
            cartForm.reRenderCart();
          } else if (typeof window.refreshCart === 'function') {
            window.refreshCart();
          }
        }
      } catch (e) {
        console.warn('[BxgyGift] enforce sync error', e);
      } finally {
        this._busy = false;
      }
    },

    /** Parent silme (full page) öncesi gift sync bilgisini sakla */
    stashParentRemoval(parentKey, parentQty, giftVariantId) {
      try {
        sessionStorage.setItem(
          'bxgy_pending_parent_removal',
          JSON.stringify({ parentKey, parentQty, giftVariantId, ts: Date.now() })
        );
      } catch (e) { /* ignore */ }
    },

    async applyStashedParentRemoval() {
      let raw;
      try {
        raw = sessionStorage.getItem('bxgy_pending_parent_removal');
        sessionStorage.removeItem('bxgy_pending_parent_removal');
      } catch (e) {
        return;
      }
      if (!raw) return;
      let data;
      try {
        data = JSON.parse(raw);
      } catch (e) {
        return;
      }
      if (!data || !data.giftVariantId) return;
      if (Date.now() - (data.ts || 0) > 60000) return; // 1 dk'dan eskiyse ignore

      await this.syncGiftAfterParentChange(data.parentKey, data.parentQty, 0, data.giftVariantId);
      const cartForm = document.getElementById('AjaxCartForm');
      if (cartForm && typeof cartForm.reRenderCart === 'function') {
        cartForm.reRenderCart();
      }
    },

    /**
     * CartForm.updateCartQty / cartPage (Alpine) trash click öncesi interceptor
     * @returns {Promise<boolean>} true = devam et, false = işlemi iptal et
     */
    async beforeQtyChange(itemEl, newQty) {
      if (!itemEl || this._busy) return true;

      const itemKey = itemEl.dataset.id;
      const oldQty = parseInt(itemEl.dataset.qty, 10) || 0;
      const giftVariantId = itemEl.dataset.bxgyGiftVariantId;
      const isGiftTarget = itemEl.hasAttribute('data-bxgy-is-gift-target');

      // --- Y ürünü değiştiriliyor ---
      if (isGiftTarget || (!giftVariantId && itemEl.dataset.bxgyMaybeGift === 'true')) {
        const check = await this.validateGiftQtyChange(itemKey, newQty, null, oldQty);
        if (!check.allowed) {
          this.showWarning(check.type);
          const actualQty = parseInt(itemEl.dataset.qty, 10) || 0;
          itemEl.querySelectorAll('.qty').forEach((input) => {
            input.value = actualQty;
          });
          return false;
        }
        return true;
      }

      // --- X (parent) ürünü değiştiriliyor → işlemden sonra sync yapılacak ---
      // Ana ürün stok limiti: sessizce engelle (BXGY modalı + Shopify 422 banner yok)
      if (giftVariantId) {
        if (newQty > oldQty) {
          const parentStockLimit = this.getStockLimitFromDom(itemEl);
          if (parentStockLimit !== Infinity && newQty > parentStockLimit) {
            itemEl.querySelectorAll('.qty').forEach((input) => {
              input.value = oldQty;
            });
            return false;
          }
        }

        itemEl.dataset.bxgyPendingSync = '1';
        itemEl.dataset.bxgyOldQty = String(oldQty);
        itemEl.dataset.bxgyNewQty = String(newQty);
        itemEl.dataset.bxgySyncGiftVariant = giftVariantId;
      }

      return true;
    },
    /**
     * Qty change + reRender sonrası parent sync tetikler
     * (component-cart.js kendi update fonksiyonunda bunu inline çağırdığı için
     *  şu an aktif olarak çağrılmıyor; API tamlığı için tutuluyor.)
     */
    async afterQtyChange(itemEl) {
      if (!itemEl || itemEl.dataset.bxgyPendingSync !== '1') return;

      const oldQty = parseInt(itemEl.dataset.bxgyOldQty, 10) || 0;
      const newQty = parseInt(itemEl.dataset.bxgyNewQty, 10) || 0;
      const giftVariantId = itemEl.dataset.bxgySyncGiftVariant;
      const parentKey = itemEl.dataset.id;

      itemEl.dataset.bxgyPendingSync = '0';

      if (!giftVariantId || oldQty === newQty) return;

      this._busy = true;
      try {
        await this.syncGiftAfterParentChange(parentKey, oldQty, newQty, giftVariantId);
        // Sync sonrası sepeti yenile
        const cartForm = document.getElementById('AjaxCartForm');
        if (cartForm && typeof cartForm.reRenderCart === 'function') {
          cartForm.reRenderCart();
        } else if (typeof window.refreshCart === 'function') {
          window.refreshCart();
        } else {
          window.location.reload();
        }
      } catch (e) {
        console.warn('[BxgyGift] sync error', e);
      } finally {
        this._busy = false;
      }
    }
  };

  window.KaracaBxgy = BxgyGift;

  document.addEventListener('DOMContentLoaded', () => {
    const modal = document.getElementById('bxgyGiftWarningModal');
    if (modal) {
      modal.querySelectorAll('[data-bxgy-close]').forEach((btn) => {
        btn.addEventListener('click', () => BxgyGift.closeWarning());
      });
    }
    // Sepet yenilendikten sonra bekleyen BXGY uyarısını göster.
    // NOT: component-cart.js reRenderCart() 'cart-updated' event'ini #AjaxCartForm
    // üzerinde bubbles:false olarak dispatch ediyor (document'e bubble etmiyor),
    // bu yüzden document yerine doğrudan #AjaxCartForm'u dinliyoruz.
    // (component-cart.js zaten reRenderCart() içinden flushPendingWarning'i de
    // doğrudan çağırıyor; bu listener ek güvenlik katmanı.)
    document.getElementById('AjaxCartForm')?.addEventListener('cart-updated', () => {
      BxgyGift.flushPendingWarning();
    });
    // Full page parent remove sonrası gift sync
    BxgyGift.applyStashedParentRemoval();

    // Sepet sayfası: hızlı "Go To Basket" sonrası eksik hediye varsa tamamla
    if (document.querySelector('form[x-data="cartPage"]')) {
      BxgyGift.enforceCartGiftSync();
    }

    // Modal "Go To Basket" — hediye eklenmeden sepete gitmeyi engelle
    document.querySelector('#addedtocartpopover [data-go-to-basket]')?.closest('a')?.addEventListener('click', (e) => {
      if (!BxgyGift._pendingGiftAdd) return;
      e.preventDefault();
      const href = e.currentTarget.href;
      BxgyGift.waitForPendingGiftAdd().then(() => {
        window.location.assign(href);
      });
    });
  });
})();
/* ========== BXGY GIFT END ========== */
